import { Stack, Text } from '@taro/design'

import type { Spread } from '../../api'
import styles from './reading.module.css'

interface Props {
  spreads: Spread[]
  balance: number
  onPick: (spread: Spread) => void
}

/**
 * Каталог раскладов.
 *
 * У каждой карточки — силуэт раскладки. Это не украшение: в старой версии
 * расклады отличались только числом карт, и увидеть разницу до покупки было
 * невозможно. Силуэт показывает форму до того, как человек заплатил.
 */
export function SpreadList({ spreads, balance, onPick }: Props) {
  return (
    <Stack gap={4}>
      <Stack gap={1}>
        <Text variant="overline" tone="tertiary">
          Расклады
        </Text>
        <Text variant="display-m" as="h2">
          Что спросишь
        </Text>
      </Stack>

      <div className={styles.list}>
        {spreads.map((spread) => {
          const affordable = spread.price <= balance

          return (
            <button
              key={spread.slug}
              type="button"
              className={styles.spread}
              onClick={() => onPick(spread)}
            >
              <Silhouette spread={spread} />

              <div className={styles.spreadBody}>
                <Text variant="title-s" as="h3">
                  {spread.title}
                </Text>
                <Text variant="body-s" tone="tertiary">
                  {spread.subtitle}
                </Text>

                <div className={styles.spreadFoot}>
                  <Text variant="caption" tone="tertiary">
                    {cardWord(spread.cardCount)}
                  </Text>
                  <span
                    className={[styles.price, affordable ? null : styles.priceOut]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {spread.price === 0 ? 'бесплатно' : spread.price}
                  </span>
                </div>
              </div>
            </button>
          )
        })}
      </div>
    </Stack>
  )
}

/**
 * Силуэт раскладки: те же координаты, что и у настоящих карт, только
 * прямоугольниками. Один источник геометрии — тот, что пришёл с сервера.
 */
function Silhouette({ spread }: { spread: Spread }) {
  return (
    <div className={styles.silhouette} aria-hidden>
      {spread.positions.map((p) => (
        <span
          key={p.idx}
          className={styles.silhouetteCard}
          style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%`, rotate: `${p.rotate}deg` }}
        />
      ))}
    </div>
  )
}

function cardWord(n: number): string {
  const last = n % 10
  const teen = n % 100 >= 11 && n % 100 <= 14
  if (!teen && last === 1) return `${n} карта`
  if (!teen && last >= 2 && last <= 4) return `${n} карты`
  return `${n} карт`
}
